import React, { useEffect, useState, useCallback, useRef } from "react";
import { Activity, RefreshCw, Radio, AlertTriangle, ArrowUp, ArrowRight, Clock } from "lucide-react";
import { ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import api from "@/lib/api";
import { fmtNum, fmtFecha } from "@/lib/format";

const POLL_MS = 20000;
const VENTANAS = [
  { min: 5, label: "5 min" },
  { min: 60, label: "60 min" },
  { min: 1440, label: "24 h" },
];
const SEM = {
  verde: { label: "VERDE", color: "#4ECDC4" },
  amarillo: { label: "AMARILLO", color: "#F39C12" },
  rojo: { label: "ROJO", color: "#E74C3C" },
  sin_datos: { label: "SIN DATOS", color: "#71717A" },
};
const TENDENCIA = {
  estable: { label: "Estable", color: "#4ECDC4", Icon: ArrowRight },
  subiendo: { label: "Subiendo", color: "#F39C12", Icon: ArrowUp },
  escalando: { label: "Escalando — actuar", color: "#E74C3C", Icon: ArrowUp },
  sin_datos: { label: "Sin datos para comparar", color: "#71717A", Icon: ArrowRight },
};

const ms = (v) => (v === null || v === undefined ? "—" : `${fmtNum(v)} ms`);
const valido = (d) => d && !d.puente_pendiente;
const horaCorta = (iso) => {
  try { return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};

export const calcTendencia = (p5, p60, rojo3) => {
  if (rojo3) return "escalando";
  if (p5 === null || p5 === undefined || !p60) return "sin_datos";
  const r = p5 / p60;
  if (r > 3) return "escalando";
  if (r > 1.5) return "subiendo";
  return "estable";
};

const Semaforo = ({ estado, testid }) => {
  const s = SEM[estado] || SEM.sin_datos;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold tracking-wider border"
      style={{ color: s.color, borderColor: `${s.color}66`, background: `${s.color}14` }}>
      <span className="w-2.5 h-2.5 rounded-full animate-pulse" style={{ background: s.color }} /> {s.label}
    </span>
  );
};

const Tendencia = ({ tipo, ratio, testid }) => {
  const t = TENDENCIA[tipo];
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: t.color }}>
      <t.Icon className="w-4 h-4" /> {t.label}
      {ratio !== null && <span className="font-mono-ax text-[10px] text-[#71717A] font-normal">({ratio.toFixed(2)}× vs 60 min)</span>}
    </span>
  );
};

const Metrica = ({ label, value, testid }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] text-[#71717A] uppercase tracking-[0.2em]">{label}</span>
    <span data-testid={testid} className="font-mono-ax text-lg text-[#F5F5F7]">{value}</span>
  </div>
);

const Panel = ({ titulo, bloque, semaforo, tendencia, ratio, prefix }) => (
  <div data-testid={`panel-${prefix}`} className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5">
    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
      <span className="text-sm text-[#D4AF37] uppercase tracking-[0.2em]">{titulo}</span>
      <div className="flex items-center gap-4 flex-wrap">
        <Semaforo estado={semaforo} testid={`semaforo-${prefix}`} />
        <Tendencia tipo={tendencia} ratio={ratio} testid={`tendencia-${prefix}`} />
      </div>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <Metrica label="Requests" value={fmtNum(bloque?.requests)} testid={`${prefix}-requests`} />
      <Metrica label="Por minuto" value={fmtNum(bloque?.por_minuto)} testid={`${prefix}-por-minuto`} />
      <Metrica label="p50" value={ms(bloque?.p50_ms)} testid={`${prefix}-p50`} />
      <Metrica label="p95" value={ms(bloque?.p95_ms)} testid={`${prefix}-p95`} />
      <Metrica label="p99" value={ms(bloque?.p99_ms)} testid={`${prefix}-p99`} />
      <Metrica label="Máx" value={ms(bloque?.max_ms)} testid={`${prefix}-max`} />
      <Metrica label="Errores 5xx" value={fmtNum(bloque?.errores_5xx)} testid={`${prefix}-5xx`} />
      <Metrica label="Lentos > 1 s" value={fmtNum(bloque?.lentos_mas_1s)} testid={`${prefix}-lentos`} />
    </div>
  </div>
);

const Grafica = ({ serie }) => (
  <div data-testid="medidor-grafica" className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] p-5">
    <span className="text-sm text-[#D4AF37] uppercase tracking-[0.2em]">Serie por minuto</span>
    {serie.length ? (
      <div className="h-72 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={serie}>
            <CartesianGrid stroke="#1F1F24" strokeDasharray="3 3" />
            <XAxis dataKey="ts" tickFormatter={horaCorta} stroke="#52525b" fontSize={11} />
            <YAxis yAxisId="ms" stroke="#52525b" fontSize={11} unit=" ms" width={70} />
            <YAxis yAxisId="req" orientation="right" stroke="#52525b" fontSize={11} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #1F1F24", color: "#F5F5F7" }} labelFormatter={fmtFecha} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="req" dataKey="requests" name="Requests" fill="#D4AF37" fillOpacity={0.25} />
            <Line yAxisId="ms" type="monotone" dataKey="p95_ms" name="p95 (ms)" stroke="#E74C3C" strokeWidth={2} dot={false} connectNulls />
            <Line yAxisId="ms" type="monotone" dataKey="p50_ms" name="p50 (ms)" stroke="#4ECDC4" strokeWidth={2} dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    ) : <p className="text-[#52525b] text-sm py-10 text-center">—</p>}
  </div>
);

const TablaRutas = ({ rutas }) => (
  <div data-testid="medidor-tabla" className="rounded-xl bg-[#0A0A0A] border border-[#1F1F24] overflow-x-auto">
    <div className="px-5 pt-5 text-sm text-[#D4AF37] uppercase tracking-[0.2em]">Rutas más lentas</div>
    <table className="w-full text-sm mt-3 min-w-[820px]">
      <thead>
        <tr className="text-[10px] text-[#71717A] uppercase tracking-wider text-left border-b border-[#1F1F24]">
          {["Método", "Ruta", "Req", "/min", "p50", "p95", "p99", "Máx", "5xx", "> 1 s"].map((h) => <th key={h} className="px-4 py-2 font-medium">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {rutas.length ? rutas.map((r, i) => (
          <tr key={`${r.metodo}-${r.ruta}`} data-testid={`ruta-${i}`} className="border-b border-[#1F1F24]/60 hover:bg-white/[0.02]">
            <td className="px-4 py-2 font-mono-ax text-[11px] text-[#A1A1AA]">{r.metodo}</td>
            <td className="px-4 py-2 font-mono-ax text-[12px] text-[#F5F5F7]">{r.ruta}</td>
            <td className="px-4 py-2 font-mono-ax">{fmtNum(r.requests)}</td>
            <td className="px-4 py-2 font-mono-ax">{fmtNum(r.por_minuto)}</td>
            <td className="px-4 py-2 font-mono-ax">{ms(r.p50_ms)}</td>
            <td className="px-4 py-2 font-mono-ax" style={{ color: r.p95_ms >= 1000 ? "#E74C3C" : undefined }}>{ms(r.p95_ms)}</td>
            <td className="px-4 py-2 font-mono-ax">{ms(r.p99_ms)}</td>
            <td className="px-4 py-2 font-mono-ax">{ms(r.max_ms)}</td>
            <td className="px-4 py-2 font-mono-ax" style={{ color: r.errores_5xx ? "#E74C3C" : undefined }}>{fmtNum(r.errores_5xx)}</td>
            <td className="px-4 py-2 font-mono-ax">{fmtNum(r.lentos_mas_1s)}</td>
          </tr>
        )) : (
          <tr><td colSpan={10} className="px-4 py-8 text-center text-[#52525b]">—</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

const useRachaRojo = () => {
  const [racha, setRacha] = useState({ general: 0, ocr: 0 });
  const registrar = useCallback((d5) => {
    if (!valido(d5) || d5._stale) return;
    setRacha((r) => ({
      general: d5.semaforo === "rojo" ? r.general + 1 : 0,
      ocr: d5.ocr?.semaforo === "rojo" ? r.ocr + 1 : 0,
    }));
  }, []);
  return [racha, registrar];
};

const Medidor = () => {
  const [ventana, setVentana] = useState(5);
  const [lecturas, setLecturas] = useState({});
  const [cargando, setCargando] = useState(false);
  const [racha, registrar] = useRachaRojo();
  const ventanaRef = useRef(ventana);
  ventanaRef.current = ventana;

  const cargar = useCallback(async () => {
    setCargando(true);
    const mins = Array.from(new Set([5, 60, ventanaRef.current]));
    const res = await Promise.all(mins.map((m) => api.get(`/comando/latencia?minutos=${m}`).then((r) => r.data).catch(() => null)));
    const nuevo = {};
    mins.forEach((m, i) => { nuevo[m] = res[i]; });
    setLecturas((prev) => ({ ...prev, ...nuevo }));
    registrar(nuevo[5]);
    setCargando(false);
  }, [registrar]);

  useEffect(() => { document.title = "Medidor En Vivo · AXIORA"; }, []);

  useEffect(() => {
    cargar();
    const t = setInterval(cargar, POLL_MS);
    return () => clearInterval(t);
  }, [cargar]);

  useEffect(() => {
    if (!lecturas[ventana]) { cargar(); }
  }, [ventana]); // eslint-disable-line react-hooks/exhaustive-deps

  const d = lecturas[ventana];
  const d5 = lecturas[5];
  const d60 = lecturas[60];
  const pend = d && d.puente_pendiente;
  const ok = valido(d);
  const comp = valido(d5) && valido(d60);
  const ratioDe = (a, b) => (a !== null && a !== undefined && b ? a / b : null);
  const rG = comp ? ratioDe(d5.general?.p95_ms, d60.general?.p95_ms) : null;
  const rO = comp ? ratioDe(d5.ocr?.p95_ms, d60.ocr?.p95_ms) : null;
  const rojo3G = racha.general >= 3;
  const rojo3O = racha.ocr >= 3;
  const tG = comp ? calcTendencia(d5.general?.p95_ms, d60.general?.p95_ms, rojo3G) : (rojo3G ? "escalando" : "sin_datos");
  const tO = comp ? calcTendencia(d5.ocr?.p95_ms, d60.ocr?.p95_ms, rojo3O) : (rojo3O ? "escalando" : "sin_datos");

  return (
    <div className="p-6 pb-32 space-y-5">
      {rojo3G && (
        <div data-testid="medidor-banner-rojo" className="flex items-center gap-3 px-5 py-4 rounded-xl bg-[#E74C3C]/15 border border-[#E74C3C]/50 text-[#E74C3C]">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span className="text-sm font-semibold">SEMÁFORO EN ROJO — {racha.general} lecturas seguidas. El sistema está degradado: actuar ya.</span>
        </div>
      )}

      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif-ax text-4xl text-[#F5F5F7] tracking-wide flex items-center gap-3">
            <Activity className="w-7 h-7 text-[#D4AF37]" /> Medidor En Vivo
          </h2>
          <p className="text-xs text-[#71717A] mt-0.5 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Refresco cada 20 s · generado {ok ? fmtFecha(d.generado) : "—"} — vía puente
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {VENTANAS.map((v) => (
            <button key={v.min} data-testid={`ventana-${v.min}`} onClick={() => setVentana(v.min)}
              className={`px-4 py-2 rounded-lg text-sm border transition-colors ${ventana === v.min ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#1F1F24] text-[#71717A] hover:text-[#F5F5F7]"}`}>
              {v.label}
            </button>
          ))}
          <button data-testid="medidor-refresh" onClick={cargar} disabled={cargando}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#1F1F24] text-[#A1A1AA] text-sm hover:border-[#4ECDC4]/40 hover:text-[#4ECDC4] disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${cargando ? "animate-spin" : ""}`} /> Actualizar
          </button>
        </div>
      </header>

      {pend && (
        <div data-testid="medidor-pendiente" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> PUENTE PENDIENTE — el medidor no está disponible ahora.
        </div>
      )}
      {ok && d._stale && (
        <div data-testid="medidor-stale" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-[#F39C12]/10 border border-[#F39C12]/30 text-[#F39C12] text-sm">
          <Radio className="w-4 h-4" /> Reconectando — mostrando la última lectura real ({fmtFecha(d._actualizado_at)}).
        </div>
      )}

      <Panel titulo={`General · ventana ${VENTANAS.find((v) => v.min === ventana).label}`} bloque={ok ? d.general : null}
        semaforo={ok ? d.semaforo : "sin_datos"} tendencia={tG} ratio={rG} prefix="general" />
      <Panel titulo="OCR" bloque={ok ? d.ocr : null}
        semaforo={ok ? d.ocr?.semaforo : "sin_datos"} tendencia={tO} ratio={rO} prefix="ocr" />
      <p className="text-[11px] text-[#52525b] -mt-2">La tendencia compara siempre el p95 de los últimos 5 min con el de 60 min.</p>

      <Grafica serie={ok ? d.serie_por_minuto || [] : []} />
      <TablaRutas rutas={ok ? d.rutas_mas_lentas || [] : []} />
      {ok && d.notas && <p data-testid="medidor-notas" className="text-[11px] text-[#52525b]">{d.notas}</p>}
    </div>
  );
};

export default Medidor;
